/**
 * Operator-facing labels — collection stage recommendation (read + dismiss only).
 */

import type { CollectionRecommendedStage } from '@shared/collection/collection-recommendation-types';
import type { CollectionRecommendationRationalePayload } from '@shared/collection/collection-recommendation-types';
import type { CollectionStageRecommendationSourceFactsProjection } from '@shared/collection/collection-stage-recommendation-projection-types';
import { normalizeCollectionRecommendationStage } from '@shared/collection/collection-recommendation-stage';

export const COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS = {
  sectionTitle: '단계 권고',
  sectionSubtitle: '참고 · 자동 실행 없음',
  boundaryCopy:
    '권고는 시스템이 제안한 다음 단계입니다. 케이스 단계 변경, 법적 조치, 연락·약속 실행은 포함하지 않습니다.',
  loading: '단계 권고를 불러오는 중…',
  loadFailed: '단계 권고를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  retryAction: '다시 불러오기',
  emptyActive: '활성 단계 권고가 없습니다.',
  emptyHistory: '단계 권고 이력이 없습니다.',
  permissionUnavailable: '단계 권고를 볼 권한이 없습니다.',
  recommendedStage: '권고 단계',
  rationale: '근거',
  sourceFacts: '근거 사실 요약',
  generatedAt: '생성 시각',
  caveats: '주의',
  historyTitle: '권고 이력',
  statusActive: '활성',
  statusDismissed: '해제됨',
  statusExpired: '만료',
  dismissAction: '권고 해제',
  dismissSubmitting: '해제 중…',
  dismissConfirmTitle: '단계 권고를 해제할까요?',
  dismissConfirmBody:
    '이 작업은 권고만 해제합니다. 수금 케이스 단계나 고객 계정 상태는 변경하지 않습니다.',
  dismissConfirmOk: '권고 해제',
  dismissConfirmCancel: '취소',
  dismissFailed: '권고 해제에 실패했습니다. 잠시 후 다시 시도해 주세요.',
  unknownRationale: '시스템 권고 근거가 제공되었습니다. 세부 코드는 진단용으로만 표시됩니다.',
  unknownCaveat: '추가 주의 사항이 있습니다. 자동 실행되지 않습니다.',
  rationaleCodeDiagnosticPrefix: '코드',
  emptyValue: '—',
  daysPastDue: '연체 일수',
  completedAttempts: '완료된 연락 시도',
  hasActiveValidPending: '유효한 대기 약속',
  isOverdue: '연체 여부',
  brokenCount: '미이행 약속',
  yes: '예',
  no: '아니오',
} as const;

const RECOMMENDED_STAGE_LABELS: Record<CollectionRecommendedStage, string> = {
  reminder: '리마인더',
  visit: '고객 방문',
  revisit: '후속 방문',
  final_notice: '최종 통지',
  legal_prep: '법무 준비 검토',
};

const RATIONALE_LABELS: Record<string, string> = {
  AGING_REMINDER_THRESHOLD: '연체 기준에 따라 리마인더 단계를 검토할 수 있습니다.',
  AGING_VISIT_THRESHOLD: '연체 일수와 연락 이력에 따라 고객 방문 단계를 검토할 수 있습니다.',
  AGING_REVISIT_THRESHOLD: '연체 일수와 연락 이력에 따라 후속 방문 단계를 검토할 수 있습니다.',
  AGING_FINAL_NOTICE_PREP: '연체·연락 이력에 따라 최종 통지 단계를 검토할 수 있습니다.',
  AGING_LEGAL_PREP_THRESHOLD:
    '연체·연락·케이스 이력에 따라 법무 준비 검토 단계를 검토할 수 있습니다.',
  BROKEN_COMMITMENT_REVISIT_STAGE: '미이행 약속이 있어 후속 방문 단계를 검토할 수 있습니다.',
  SUPPRESSED_CASE_INELIGIBLE: '현재 케이스 상태에서는 권고가 생성되지 않습니다.',
  SUPPRESSED_ACTIVE_CONTACT: '진행 중인 연락 흐름이 있어 권고가 억제되었습니다.',
  SUPPRESSED_VALID_COMMITMENT: '유효한 대기 약속이 있어 권고가 억제되었습니다.',
};

export const COLLECTION_STAGE_RECOMMENDATION_CAVEAT_LABELS: Record<string, string> = {
  legal_prep_internal_review_only:
    '내부 준비 및 검토용입니다. 이 권고는 법적 조치나 제소를 승인하지 않습니다.',
};

export function labelRecommendedStageForRecommendation(stage: string | null | undefined): string {
  if (!stage?.trim()) {
    return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.emptyValue;
  }
  const trimmed = stage.trim();
  if (trimmed === 'final') {
    return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.emptyValue;
  }
  const normalized = normalizeCollectionRecommendationStage(stage);
  if (normalized.ok) {
    return RECOMMENDED_STAGE_LABELS[normalized.stage];
  }
  return stage.trim();
}

export function labelRecommendationRationale(
  rationaleCode: string,
  rationalePayload?: CollectionRecommendationRationalePayload
): string {
  const mapped = RATIONALE_LABELS[rationaleCode.trim()];
  if (mapped) {
    const ruleId =
      rationalePayload && typeof rationalePayload.ruleId === 'string'
        ? rationalePayload.ruleId.trim()
        : '';
    return ruleId ? `${mapped} (${ruleId})` : mapped;
  }
  return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.unknownRationale;
}

export function labelRecommendationCaveat(caveatCode: string): string {
  const mapped = COLLECTION_STAGE_RECOMMENDATION_CAVEAT_LABELS[caveatCode.trim()];
  return mapped ?? COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.unknownCaveat;
}

export function labelRecommendationStatus(status: 'active' | 'dismissed' | 'expired'): string {
  switch (status) {
    case 'active':
      return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.statusActive;
    case 'dismissed':
      return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.statusDismissed;
    case 'expired':
      return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.statusExpired;
    default:
      return status;
  }
}

export function formatRecommendationTimestamp(iso: string | null | undefined): string {
  if (!iso?.trim()) {
    return COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.emptyValue;
  }
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

export function formatRecommendationSourceFactsSummary(
  facts: CollectionStageRecommendationSourceFactsProjection
): string[] {
  const labels = COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS;
  const lines: string[] = [];

  if (facts.daysPastDue != null) {
    lines.push(`${labels.daysPastDue}: ${facts.daysPastDue}일`);
  }
  lines.push(`${labels.completedAttempts}: ${facts.completedAttempts}`);
  lines.push(
    `${labels.hasActiveValidPending}: ${facts.hasActiveValidPending ? labels.yes : labels.no}`
  );
  lines.push(`${labels.isOverdue}: ${facts.isOverdue ? labels.yes : labels.no}`);
  lines.push(`${labels.brokenCount}: ${facts.brokenCount}`);

  return lines;
}
